import type { CSSProperties } from "react";

type IconName =
  | "plus" | "search" | "branch" | "x" | "more" | "expand" | "shrink"
  | "split" | "split-h" | "play" | "pause" | "lock" | "eye" | "terminal"
  | "sparkles" | "users" | "folder" | "arrow-up" | "arrow-down" | "arrow-right"
  | "check" | "pull" | "git-commit" | "play-circle" | "settings" | "chevron-down"
  | "info" | "key";

interface IcoProps {
  name: IconName;
  size?: number;
  className?: string;
  style?: CSSProperties;
}

export function Ico({ name, size = 16, className, style }: IcoProps) {
  const common = {
    width: size, height: size, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth: 1.5,
    strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
    className, style,
  };
  switch (name) {
    case "plus": return <svg {...common}><path d="M12 5v14M5 12h14"/></svg>;
    case "search": return <svg {...common}><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>;
    case "branch": return <svg {...common}><circle cx="6" cy="6" r="2"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="9" r="2"/><path d="M6 8v8M6 16c6 0 12-1 12-5"/></svg>;
    case "x": return <svg {...common}><path d="M18 6 6 18M6 6l12 12"/></svg>;
    case "more": return <svg {...common}><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></svg>;
    case "expand": return <svg {...common}><path d="M3 9V5a2 2 0 0 1 2-2h4M21 9V5a2 2 0 0 0-2-2h-4M3 15v4a2 2 0 0 0 2 2h4M21 15v4a2 2 0 0 1-2 2h-4"/></svg>;
    case "shrink": return <svg {...common}><path d="M5 9h4V5M19 9h-4V5M5 15h4v4M19 15h-4v4"/></svg>;
    case "split":  return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 3v18"/></svg>;
    case "split-h":return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 12h18"/></svg>;
    case "play":   return <svg {...common}><path d="m6 4 14 8-14 8z" fill="currentColor"/></svg>;
    case "pause":  return <svg {...common}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>;
    case "lock":   return <svg {...common}><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>;
    case "eye":    return <svg {...common}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>;
    case "terminal":return <svg {...common}><path d="m4 17 6-6-6-6M12 19h8"/></svg>;
    case "sparkles":return <svg {...common}><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/></svg>;
    case "users":  return <svg {...common}><circle cx="9" cy="8" r="3"/><path d="M3 21v-1a6 6 0 0 1 12 0v1"/><circle cx="17" cy="8" r="2.5"/><path d="M21 19v-1a4 4 0 0 0-3-3.87"/></svg>;
    case "folder": return <svg {...common}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>;
    case "arrow-up":    return <svg {...common}><path d="M12 19V5M5 12l7-7 7 7"/></svg>;
    case "arrow-down":  return <svg {...common}><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
    case "arrow-right": return <svg {...common}><path d="M5 12h14M13 5l7 7-7 7"/></svg>;
    case "check":  return <svg {...common}><path d="M20 6 9 17l-5-5"/></svg>;
    case "pull":   return <svg {...common}><path d="M12 3v12M5 10l7 7 7-7"/><path d="M5 21h14"/></svg>;
    case "git-commit": return <svg {...common}><circle cx="12" cy="12" r="3"/><path d="M3 12h6M15 12h6"/></svg>;
    case "play-circle": return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="m10 8 6 4-6 4z" fill="currentColor"/></svg>;
    case "settings": return <svg {...common}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;
    case "chevron-down": return <svg {...common}><path d="m6 9 6 6 6-6"/></svg>;
    case "info":   return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>;
    case "key":    return <svg {...common}><circle cx="8" cy="15" r="4"/><path d="m10.85 12.15 8.15-8.15 2 2-2 2 2 2-3 3"/></svg>;
  }
}

export function LSIcon({ kind, size = 14 }: { kind: "2x2" | "1x2" | "1x3" | "full"; size?: number }) {
  const w = 14, h = 12;
  if (kind === "full") {
    return (
      <svg width={size} height={size * (h / w)} viewBox={`0 0 ${w} ${h}`} fill="none">
        <rect x={1} y={1} width={w - 2} height={h - 2} rx={1} className="ls-cell"/>
      </svg>
    );
  }
  const cells = kind === "2x2"
    ? [[0,0],[1,0],[0,1],[1,1]]
    : kind === "1x2"
    ? [[0,0],[1,0]]
    : [[0,0],[1,0],[2,0]];
  const cols = kind === "1x3" ? 3 : 2;
  const rows = kind === "2x2" ? 2 : 1;
  const cw = (w - 2) / cols;
  const ch = (h - 2) / rows;
  return (
    <svg width={size} height={size * (h / w)} viewBox={`0 0 ${w} ${h}`} fill="none">
      {cells.map(([x, y], i) => (
        <rect key={i}
              x={1 + x! * cw + (x! ? 0.5 : 0)}
              y={1 + y! * ch + (y! ? 0.5 : 0)}
              width={cw - (cols > 1 ? 0.5 : 0)}
              height={ch - (rows > 1 ? 0.5 : 0)}
              rx={0.5}
              className="ls-cell"/>
      ))}
    </svg>
  );
}
